import * as React from 'react';
import { toast } from 'sonner';
import { createClaimSchema, formatPolicyRef, type ClaimDto, type CreateClaimInput, today } from '@ias/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AutoForm, type FieldDescriptor } from '@/components/form/auto-form';
import { claimsApi, policiesApi, useAssignableUsers } from '@/lib/api/domains';
import { useI18n } from '@/lib/i18n';

export function ClaimFormDialog({
  open,
  onOpenChange,
  claim,
  policyId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claim?: ClaimDto;
  policyId?: string;
}) {
  const { t } = useI18n();
  const { data: agents } = useAssignableUsers();
  // Cancelled policies cannot take a new claim, so they are not offered.
  const { data: policies } = policiesApi.useList({ perPage: 200, status: 'active,renewal_due,paid,expired' });

  const create = claimsApi.useCreate<CreateClaimInput>();
  const update = claimsApi.useUpdate<Partial<CreateClaimInput>>();
  const isEdit = Boolean(claim);
  const formId = 'claim-form';

  const fields: FieldDescriptor<Record<string, unknown>>[] = [
    {
      name: 'policyId',
      type: 'select',
      label: t('policy.one'),
      required: true,
      disabled: isEdit || Boolean(policyId),
      options: (policies?.data ?? []).map((policy) => ({
        value: policy.id,
        label: `${formatPolicyRef(policy.policyNumber, policy.plates)} · ${policy.clientName}`,
      })),
    },
    { name: 'claimNumber', type: 'text', label: t('claim.number') },
    { name: 'incidentDate', type: 'date', label: t('claim.incidentDate'), required: true },
    { name: 'reportDate', type: 'date', label: t('claim.reportDate') },
    { name: 'claimAmount', type: 'money', label: t('claim.amount') },
    { name: 'payoutAmount', type: 'money', label: t('claim.payout') },
    { name: 'status', type: 'enum', enum: 'claimStatus', label: t('policy.status') },
    {
      name: 'assignedAgentId',
      type: 'select',
      label: t('client.agent'),
      options: (agents ?? []).map((agent) => ({ value: agent.id, label: agent.fullName })),
    },
    { name: 'description', type: 'textarea', label: t('claim.description') },
    { name: 'notes', type: 'textarea', label: t('common.notes') },
  ];

  const submit = async (values: Record<string, unknown>) => {
    const payload = { ...values };
    for (const key of ['claimNumber', 'claimAmount', 'payoutAmount', 'assignedAgentId', 'reportDate'] as const) {
      if (!payload[key]) delete payload[key];
    }

    if (isEdit && claim) {
      await update.mutateAsync({ id: claim.id, input: payload as Partial<CreateClaimInput> });
    } else {
      await create.mutateAsync(payload as CreateClaimInput);
    }
    toast.success(t('common.saved'));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('action.edit') : t('action.newClaim')}</DialogTitle>
          <DialogDescription>{t(isEdit ? 'dialog.editClaimHint' : 'dialog.newClaimHint')}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <AutoForm
            id={formId}
            schema={isEdit ? createClaimSchema.innerType().partial() : createClaimSchema}
            defaultValues={
              {
                policyId: claim?.policyId ?? policyId ?? '',
                claimNumber: claim?.claimNumber ?? '',
                incidentDate: claim?.incidentDate ?? today(),
                reportDate: claim?.reportDate ?? today(),
                claimAmount: claim?.claimAmount ?? '',
                payoutAmount: claim?.payoutAmount ?? '',
                status: claim?.status ?? 'open',
                assignedAgentId: claim?.assignedAgentId ?? '',
                description: claim?.description ?? '',
                notes: claim?.notes ?? '',
              } as never
            }
            fields={fields}
            onSubmit={submit}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button type="submit" form={formId} loading={create.isPending || update.isPending}>
            {t('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
